import * as React from 'react';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { AdminCampaignSummary, AdminCampaignRun, CAMPAIGN_STATES } from '@app/Campaigns/types';
import { getCampaigns, getPendingApprovalRuns } from '@app/Campaigns/campaigns.api';
import { CampaignStateBadge } from '@app/Campaigns/badges';
import { ApprovalQueueTable } from '@app/Campaigns/ApprovalQueueTable';
import { relativeTime, formatDateTime } from '@app/Screens/format';
import { colors, radii, shadows } from '@app/components/designTokens';

type Tab = 'campaigns' | 'queue';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  color: colors.subtle,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap'
};
const td: React.CSSProperties = {
  padding: '13px 16px',
  fontSize: 14,
  color: colors.body,
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle'
};

export const Campaigns: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('campaigns');

  const [campaigns, setCampaigns] = useState<AdminCampaignSummary[]>([]);
  const [queue, setQueue] = useState<AdminCampaignRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState('');
  const [search, setSearch] = useState('');
  const [hovered, setHovered] = useState<number | null>(null);

  const load = useCallback(
    async (spinner: boolean) => {
      if (spinner) setLoading(true);
      try {
        const [list, q] = await Promise.all([
          getCampaigns({ state: stateFilter || undefined, q: search || undefined, limit: 200 }),
          getPendingApprovalRuns()
        ]);
        setCampaigns(list);
        setQueue(q);
        setError(null);
      } catch (e: any) {
        setError(e?.message || 'Failed to load campaigns');
        if (!spinner) toast.error('Could not refresh campaigns');
      } finally {
        if (spinner) setLoading(false);
      }
    },
    [stateFilter, search]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const summary = useMemo(() => {
    const active = campaigns.filter(c => c.currentState === 'ACTIVE').length;
    return { total: campaigns.length, active };
  }, [campaigns]);

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 700, color: colors.title, margin: 0 },
    subtle: { fontSize: 13, color: colors.subtle },
    refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: `1px solid ${colors.border}`, borderRadius: radii.button, background: '#fff', color: colors.body, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    tabs: { display: 'flex', gap: 4, borderBottom: `1px solid ${colors.border}`, marginBottom: 16 },
    toolbar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
    search: { flex: 1, minWidth: 220, maxWidth: 340, padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, outline: 'none' },
    select: { padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, background: '#fff', color: colors.body },
    card: { background: '#fff', borderRadius: radii.card, boxShadow: shadows.softCard, border: `1px solid ${colors.border}`, overflow: 'hidden' }
  };

  const tabBtn = (key: Tab, label: string, badge?: number) => {
    const active = tab === key;
    return (
      <button
        onClick={() => setTab(key)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          border: 'none',
          background: 'transparent',
          color: active ? colors.accent : colors.muted,
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          borderBottom: `2px solid ${active ? colors.accent : 'transparent'}`,
          marginBottom: -1
        }}
      >
        {label}
        {badge != null && badge > 0 && (
          <span style={{ background: colors.warning, color: '#fff', borderRadius: 9999, fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>
            {badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Campaigns</h1>
          <div style={styles.subtle}>
            {loading ? 'Loading…' : `${summary.total} loaded · ${summary.active} active · ${queue.length} runs awaiting approval`}
          </div>
        </div>
        <button style={styles.refreshBtn} onClick={() => load(true)}>
          <SyncIcon className={loading ? 'logo-bolt-pulse' : undefined} /> Refresh
        </button>
      </div>

      <div style={styles.tabs}>
        {tabBtn('campaigns', 'All campaigns')}
        {tabBtn('queue', 'Approval queue', queue.length)}
      </div>

      {error && (
        <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 12 }}>{error}</div>
      )}

      {tab === 'campaigns' ? (
        <>
          <div style={styles.toolbar}>
            <input style={styles.search} placeholder="Search by name or owner…" value={search} onChange={e => setSearch(e.target.value)} />
            <select style={styles.select} value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
              <option value="">All states</option>
              {CAMPAIGN_STATES.map(s => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.card}>
            {loading ? (
              <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>Loading campaigns…</div>
            ) : campaigns.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>No campaigns match these filters.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th style={th}>Campaign</th>
                      <th style={th}>Owner</th>
                      <th style={th}>State</th>
                      <th style={th}>Runs</th>
                      <th style={th}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map(c => (
                      <tr
                        key={c.campaignId}
                        onClick={() => navigate(`/campaigns/${c.campaignId}`)}
                        onMouseEnter={() => setHovered(c.campaignId)}
                        onMouseLeave={() => setHovered(null)}
                        style={{ cursor: 'pointer', background: hovered === c.campaignId ? '#f8fafc' : 'transparent' }}
                      >
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{c.name || `Campaign #${c.campaignId}`}</div>
                          <div style={{ fontSize: 12, color: colors.subtle }}>
                            {c.adName ? `Ad: ${c.adName}` : `id ${c.campaignId}`}
                            {c.type ? ` · ${c.type}` : ''}
                          </div>
                        </td>
                        <td style={td}>
                          {c.ownerName || c.ownerEmail ? (
                            <>
                              <div>{c.ownerName || '—'}</div>
                              <div style={{ fontSize: 12, color: colors.subtle }}>{c.ownerEmail}</div>
                            </>
                          ) : (
                            <span style={{ color: colors.placeholder }}>—</span>
                          )}
                        </td>
                        <td style={td}>
                          <CampaignStateBadge state={c.currentState} />
                        </td>
                        <td style={td}>
                          <span style={{ fontWeight: 600 }}>{c.runsTotal}</span>
                          <div style={{ fontSize: 12, color: colors.subtle }}>
                            {c.runsInProgress} live · {c.runsPendingApproval} pending
                          </div>
                        </td>
                        <td style={td} title={formatDateTime(c.createDateTime)}>
                          {relativeTime(c.createDateTime)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div style={styles.card}>
          <ApprovalQueueTable runs={queue} loading={loading} onSelect={cid => navigate(`/campaigns/${cid}`)} />
        </div>
      )}
    </div>
  );
};
